/* eslint-disable react/prop-types */
// Self-contained searchable dropdown for the Twilio settings modal. A trigger
// button opens a panel anchored below it; the panel has an auto-focused search
// input that filters `options` by a case-insensitive substring match against
// each option's search text (name + email by default). Clicking a row — or
// pressing Enter on the keyboard-highlighted row — calls onSelect and closes.
//
// Kept generic (getKey/getPrimary/getSecondary/getSearchText props) so other
// sections (e.g. Routing's team picker) could adopt it later without changes.
// Colors come only from tokens.css via ta-* classes; no hardcoded colors here.

import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FaSearch } from 'react-icons/fa';
import { useDropdown } from './settings_hooks';

// The panel anchors to the trigger but renders in a portal on document.body, so
// its width never shrinks below this even when the trigger is narrow.
const MIN_PANEL_WIDTH = 280;
// Gap between the trigger and the panel, and the viewport padding kept on flip.
const GAP = 4;

export default function SearchableDropdown({
  options,
  onSelect,
  placeholder = 'Add member…',
  selectedLabel = '',
  emptyPlaceholder = 'No options',
  noMatchesLabel = 'No matches',
  searchPlaceholder = 'Search…',
  disabled = false,
  getKey = (o) => o.uuid,
  getPrimary = (o) => o.name || o.email || o.uuid,
  getSecondary = (o) => o.email || '',
  getSearchText = (o) => `${o.name || ''} ${o.email || ''}`
}) {
  const { open, setOpen, ref, panelRef } = useDropdown();
  const [query, setQuery] = useState('');
  const [highlight, setHighlight] = useState(0);
  // Fixed-position box for the portaled panel, measured from the trigger. null
  // until the first measurement so the panel stays hidden (no flash at 0,0).
  const [pos, setPos] = useState(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => getSearchText(o).toLowerCase().includes(q));
  }, [options, query, getSearchText]);

  // Reset transient state whenever the panel opens; focus the search input.
  useEffect(() => {
    if (!open) {
      setQuery('');
      setHighlight(0);
      return undefined;
    }
    setHighlight(0);
    const id = window.setTimeout(() => {
      if (inputRef.current) inputRef.current.focus();
    }, 0);
    return () => window.clearTimeout(id);
  }, [open]);

  // Keep the highlight in range as the filtered list shrinks/grows.
  useEffect(() => {
    setHighlight((h) => (filtered.length === 0 ? 0 : Math.min(h, filtered.length - 1)));
  }, [filtered.length]);

  // Scroll the highlighted row into view.
  useEffect(() => {
    if (!open || !listRef.current) return;
    const node = listRef.current.children[highlight];
    if (node && node.scrollIntoView) node.scrollIntoView({ block: 'nearest' });
  }, [highlight, open]);

  // Position the portaled panel in fixed coordinates derived from the trigger's
  // current viewport rect. Opens below by default; if the panel would overflow
  // the viewport bottom and there's more room above, it flips to open upward.
  // Also clamps horizontally so a wide panel near the right edge stays on screen.
  const computePosition = useCallback(() => {
    const anchor = ref.current;
    if (!anchor) return;
    const rect = anchor.getBoundingClientRect();
    const width = Math.max(rect.width, MIN_PANEL_WIDTH);
    const panelHeight = panelRef.current ? panelRef.current.offsetHeight : 0;
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const openUp = panelHeight > 0 && spaceBelow < panelHeight + GAP && spaceAbove > spaceBelow;
    let { left } = rect;
    if (left + width > window.innerWidth - GAP * 2) {
      left = Math.max(GAP * 2, window.innerWidth - GAP * 2 - width);
    }
    const top = openUp ? Math.max(GAP, rect.top - GAP - panelHeight) : rect.bottom + GAP;
    setPos({ top, left, width });
  }, [ref, panelRef]);

  // Measure on open (before paint, so there's no visible jump), and keep the
  // panel glued to the trigger while ancestors scroll or the window resizes.
  useLayoutEffect(() => {
    if (!open) {
      setPos(null);
      return undefined;
    }
    computePosition();
    const onChange = () => computePosition();
    window.addEventListener('resize', onChange);
    // Capture phase so scrolling of any ancestor (e.g. the modal body) is caught.
    window.addEventListener('scroll', onChange, true);
    return () => {
      window.removeEventListener('resize', onChange);
      window.removeEventListener('scroll', onChange, true);
    };
  }, [open, computePosition]);

  const choose = (option) => {
    if (!option) return;
    onSelect(option);
    setOpen(false);
  };

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((h) => (filtered.length === 0 ? 0 : (h + 1) % filtered.length));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) =>
        filtered.length === 0 ? 0 : (h - 1 + filtered.length) % filtered.length
      );
    } else if (e.key === 'Enter') {
      e.preventDefault();
      choose(filtered[highlight]);
    }
  };

  const triggerDisabled = disabled || options.length === 0;

  return (
    <div className="ta-dropdown" ref={ref}>
      <button
        type="button"
        className="ta-dropdown-trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
        disabled={triggerDisabled}
        onClick={() => setOpen((v) => !v)}
      >
        {selectedLabel ? (
          <span className="ta-dropdown-val">{selectedLabel}</span>
        ) : (
          <span className="ta-dropdown-ph">
            {options.length === 0 ? emptyPlaceholder : placeholder}
          </span>
        )}
      </button>

      {open &&
        createPortal(
          <div
            ref={panelRef}
            className="ta-dropdown-panel"
            role="listbox"
            style={{
              position: 'fixed',
              top: pos ? pos.top : 0,
              left: pos ? pos.left : 0,
              width: pos ? pos.width : MIN_PANEL_WIDTH,
              visibility: pos ? 'visible' : 'hidden'
            }}
          >
            <div className="ta-dropdown-search">
              <FaSearch size={12} />
              <input
                ref={inputRef}
                type="text"
                className="ta-dropdown-search-input"
                placeholder={searchPlaceholder}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={onKeyDown}
              />
            </div>
            <div className="ta-dropdown-options" ref={listRef}>
              {filtered.map((o, i) => (
                <button
                  key={getKey(o)}
                  type="button"
                  role="option"
                  aria-selected={i === highlight}
                  className={`ta-dropdown-option ${i === highlight ? 'ta-dropdown-option-active' : ''}`}
                  onMouseEnter={() => setHighlight(i)}
                  onClick={() => choose(o)}
                >
                  <span className="ta-dropdown-option-name">{getPrimary(o)}</span>
                  {getSecondary(o) && (
                    <span className="ta-dropdown-option-email">{getSecondary(o)}</span>
                  )}
                </button>
              ))}
              {filtered.length === 0 && <div className="ta-dropdown-empty">{noMatchesLabel}</div>}
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}
